'use client';

import * as Fathom from 'fathom-client';
import { usePathname, useSearchParams } from 'next/navigation';
import { Suspense, useEffect, useRef } from 'react';
import { buildPageviewUrl } from '@/lib/fathom';

// Fathom site IDs ship in the page source, so they are not secret. The
// production site is the default; NEXT_PUBLIC_FATHOM_SITE_ID overrides it for
// a separate Fathom property.
const SITE_ID = process.env.NEXT_PUBLIC_FATHOM_SITE_ID || 'USGWIAGB';

// Optional Fathom custom-domain script URL (e.g. https://stats.ullr.ski/script.js).
// Pointing at a custom domain is Fathom's supported way to stop ad blockers
// from dropping the tracker; unset falls back to cdn.usefathom.com.
const SCRIPT_URL = process.env.NEXT_PUBLIC_FATHOM_SCRIPT_URL;

function FathomPageviews() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastTracked = useRef<string | null>(null);

  useEffect(() => {
    // `auto: false` — Fathom's SPA mode (`data-spa="auto"`) detects navigation
    // by wrapping window.history.pushState. That worked when this app was a
    // CRA/react-router build, but the App Router does not route its own
    // client-side navigations through that wrapper, so after the Next
    // migration only full page loads were reaching Fathom. Pageviews are
    // driven off the router below instead — the setup fathom-client
    // documents for the app directory.
    Fathom.load(SITE_ID, {
      auto: false,
      ...(SCRIPT_URL ? { url: SCRIPT_URL } : {}),
    });
  }, []);

  useEffect(() => {
    if (!pathname) return;

    const url = buildPageviewUrl(
      window.location.origin,
      pathname,
      searchParams?.toString() ?? ''
    );

    // A query-only change (filters, tab state) that the allow-list strips away
    // resolves to the same URL — don't double-count it.
    if (url === lastTracked.current) return;

    // document.referrer is frozen at the entry page for the whole SPA session,
    // so reusing it on every navigation would re-count the original referral
    // once per page. After the first pageview the referrer is the page the
    // router came from (same-origin, which Fathom ignores as a self-referral).
    const referrer = lastTracked.current ?? document.referrer;
    lastTracked.current = url;

    Fathom.trackPageview({ url, referrer });
  }, [pathname, searchParams]);

  return null;
}

export default function FathomAnalytics() {
  // Local dev traffic would otherwise land in the production Fathom site.
  if (process.env.NODE_ENV !== 'production') return null;

  // useSearchParams() opts its component into client-side rendering; without a
  // Suspense boundary that deopts every route to dynamic rendering.
  return (
    <Suspense fallback={null}>
      <FathomPageviews />
    </Suspense>
  );
}
